<template>
  <!-- Sort contract links: what each existing system–contract link covers, with suggestions (US 9370) -->
  <div class="pm-page so-page">
    <div class="cl-head">
      <router-link to="/systems" class="v2-link cl-back"><font-awesome-icon icon="angle-left"/>{{ $t('systems.title') }}</router-link>
      <h1>{{ $t('contract_links.title') }}</h1>
      <p>{{ $t('contract_links.intro') }}</p>
    </div>

    <div v-if="loaded" class="cl-tiles">
      <div class="cl-tile">
        <b>{{ counts.links }}</b><span>{{ $t('contract_links.tiles.links') }}</span>
        <small>{{ $tc('contract_links.tiles.links_sub', counts.systems_with_links, { count: counts.systems_with_links }) }}</small>
      </div>
      <div class="cl-tile is-violet">
        <b>{{ counts.single }}</b><span>{{ $t('contract_links.tiles.single') }}</span>
        <small>{{ $t('contract_links.tiles.single_sub') }}</small>
      </div>
      <div class="cl-tile is-blue">
        <b>{{ counts.multiple }}</b><span>{{ $t('contract_links.tiles.multiple') }}</span>
        <small>{{ $t('contract_links.tiles.multiple_sub') }}</small>
      </div>
      <div class="cl-tile is-amber">
        <b>{{ counts.without }}</b><span>{{ $t('contract_links.tiles.without') }}</span>
        <small>{{ $t('contract_links.tiles.without_sub') }}</small>
      </div>
    </div>

    <section class="v2-card so-card">
      <div class="pm-map__head">
        <div class="v2-seg" role="tablist">
          <span v-for="item in tabs"
              :key="item.key"
              role="tab"
              :class="{ on: tab === item.key }"
              :qid="'contract-links-tab-' + item.key"
              @click="setTab(item.key)">
            {{ $t('contract_links.tabs.' + item.key) }}
            <i class="pm-tabs__count" :class="{ 'is-warn': item.warn && item.count }">{{ item.count }}</i>
          </span>
        </div>
        <span class="sp-grow"></span>
        <div class="home-search cl-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input v-model="query" class="home-search__input" :placeholder="$t('contract_links.search')" :aria-label="$t('contract_links.search')" qid="contract-links-search"/>
        </div>
        <button type="button"
            class="btn btn-success"
            :disabled="!confirmable.length || saving"
            qid="contract-links-confirm-selected"
            @click="confirm(confirmable)">
          <font-awesome-icon icon="check"/> {{ $tc('contract_links.confirm_selected', confirmable.length, { count: confirmable.length }) }}
        </button>
      </div>

      <div class="so-table">
        <div class="so-row so-row--head cl-grid">
          <span><input type="checkbox" class="so-check" :checked="allSelected" :aria-label="$t('systems_overview.bulk.select_all')" @change="toggleAll($event.target.checked)"/></span>
          <span>{{ $t('contract_links.columns.system') }}</span>
          <span>{{ $t('contract_links.columns.contract') }}</span>
          <span>{{ $t('contract_links.columns.covers') }}</span>
          <span>{{ $t('contract_links.columns.why') }}</span>
          <span></span>
        </div>
        <div v-if="!loaded" class="home-empty">{{ $t('systems_overview.loading') }}</div>
        <template v-else>
          <div v-for="link in rows" :key="link.link_id" class="so-row cl-grid" :class="{ 'is-selected': selected.includes(link.link_id) }">
            <span>
              <input v-if="link.suggestion && !link.covers"
                  type="checkbox"
                  class="so-check"
                  :checked="selected.includes(link.link_id)"
                  :aria-label="link.contract.name"
                  @change="toggle(link.link_id, $event.target.checked)"/>
            </span>
            <span class="so-name">
              <span class="so-logo" :style="{ background: logoColor(link.system.name) }">{{ initials(link.system.name) }}</span>
              <router-link :to="{ name: 'systems.view', params: { slug: link.system.slug } }" class="so-name__link">{{ link.system.name }}</router-link>
            </span>
            <span class="so-two">
              <router-link :to="{ name: 'contracts.view', params: { slug: link.contract.slug } }" class="cl-contract">{{ link.contract.name }}</router-link>
              <small v-if="link.processing_role">{{ $t('maintenance.third_parties.processing_role.' + $options.filters.slugify(link.processing_role)) }}</small>
            </span>
            <span>
              <b-dropdown variant="link" toggle-class="cl-covers" no-caret :qid="'contract-links-covers-' + link.link_id">
                <template #button-content>
                  <span v-if="link.covers" class="sp-covers" :class="'is-' + group(link.covers)">{{ $t('systems_overview.covers.' + link.covers) }}</span>
                  <span v-else-if="link.suggestion" class="cl-sugg"><font-awesome-icon icon="sparkles"/>{{ $t('systems_overview.covers.' + link.suggestion.covers) }}</span>
                  <span v-else class="btn btn-sm btn-secondary">{{ $t('contract_links.choose') }} <font-awesome-icon icon="angle-down"/></span>
                </template>
                <b-dropdown-header>{{ $t('system_page.where.groups.runs_on') }}</b-dropdown-header>
                <b-dropdown-item v-for="value in runsOn" :key="value" :active="link.covers === value" @click="setOne(link, value)">{{ $t('systems_overview.covers.' + value) }}</b-dropdown-item>
                <b-dropdown-header>{{ $t('system_page.where.groups.access') }}</b-dropdown-header>
                <b-dropdown-item v-for="value in access" :key="value" :active="link.covers === value" @click="setOne(link, value)">{{ $t('systems_overview.covers.' + value) }}</b-dropdown-item>
                <template v-if="link.covers">
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item @click="setOne(link, null)">{{ $t('systems_overview.covers.none') }}</b-dropdown-item>
                </template>
              </b-dropdown>
            </span>
            <span class="cl-why">{{ why(link) }}</span>
            <span class="cl-action">
              <span v-if="link.covers" class="pm-status is-ok"><font-awesome-icon icon="check"/>{{ $t('contract_links.confirmed') }}</span>
              <button v-else-if="link.suggestion" type="button" class="btn btn-success btn-sm" :disabled="saving" @click="confirm([link])">{{ $t('contract_links.confirm') }}</button>
            </span>
          </div>
          <div v-if="!rows.length" class="home-empty">{{ query ? $t('contract_links.empty_search') : $t('contract_links.empty_tab') }}</div>
        </template>
      </div>
      <div v-if="loaded" class="pm-foot so-foot">
        <span>{{ $t('contract_links.note') }}</span>
        <span class="so-foot__count">{{ $t('systems_overview.showing', { shown: rows.length, total: links.length }) }}</span>
      </div>
    </section>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  const RUNS_ON = ['software_hosting', 'hosting', 'licence'];
  const ACCESS = ['support', 'works_in'];
  const LOGO_COLORS = ['#1F7A72', '#3B6FD8', '#7B5CD6', '#D9532B', '#0E7C86', '#C27C00', '#5B6573', '#C2417A'];

  export default {
    name: 'SortContractLinks',
    data: function() {
      return {
        loaded: false,
        links: [],
        counts: {},
        tab: 'clear',
        query: '',
        selected: [],
        saving: false,
        runsOn: RUNS_ON,
        access: ACCESS
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      tabs() {
        let count = filter => this.links.filter(filter).length;
        return [
          { key: 'all', count: this.links.length },
          { key: 'clear', count: count(this.filters.clear) },
          { key: 'check', count: count(this.filters.check) },
          { key: 'choose', count: count(this.filters.choose), warn: true },
          { key: 'sorted', count: count(this.filters.sorted) }
        ];
      },
      filters() {
        return {
          all: () => true,
          clear: link => !link.covers && link.suggestion && link.suggestion.confidence === 'clear',
          check: link => !link.covers && link.suggestion && link.suggestion.confidence === 'check',
          choose: link => !link.covers && !link.suggestion,
          sorted: link => !!link.covers
        };
      },
      rows() {
        let query = this.query.trim().toLowerCase();
        return this.links.filter(this.filters[this.tab]).filter(link => !query
          || link.system.name.toLowerCase().includes(query) || link.contract.name.toLowerCase().includes(query));
      },
      selectableRows() {
        return this.rows.filter(link => link.suggestion && !link.covers);
      },
      allSelected() {
        return this.selectableRows.length > 0 && this.selectableRows.every(link => this.selected.includes(link.link_id));
      },
      // Ticked rows, or all clear suggestions shown when nothing is ticked
      confirmable() {
        let ticked = this.links.filter(link => this.selected.includes(link.link_id) && link.suggestion && !link.covers);
        return ticked.length ? ticked : this.selectableRows.filter(link => link.suggestion.confidence === 'clear');
      }
    },
    watch: {
      getClient: function() {
        this.load();
      }
    },
    methods: {
      load() {
        let self = this;
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        piincHttp.get('systems/contract-links', { params: { client: this.getClient.slug } }).then(function(response) {
          self.links = response.data.links;
          self.counts = response.data.counts;
          self.loaded = true;
          self.selected = self.selected.filter(id => _.find(self.links, { link_id: id, covers: null }));
          // Start on the first tab that has something to do
          if (self.tab === 'clear' && !self.tabs[1].count) {
            self.tab = self.tabs[2].count ? 'check' : (self.tabs[3].count ? 'choose' : 'all');
          }
        }, function() {});
      },
      setTab(key) {
        this.tab = key;
        this.selected = [];
      },
      group(covers) {
        return RUNS_ON.includes(covers) ? 'runs_on' : 'access';
      },
      why(link) {
        if (link.covers) {
          return '';
        }
        if (!link.suggestion) {
          return this.$t('contract_links.why.none');
        }
        return this.$t('contract_links.why.' + link.suggestion.reason, { word: link.suggestion.word });
      },
      initials(name) {
        let words = (name || '').trim().split(/\s+/).filter(Boolean);
        return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : (words[0] || '?').slice(0, 2).toUpperCase();
      },
      logoColor(name) {
        let sum = 0;
        for (let i = 0; i < (name || '').length; i++) {
          sum += name.charCodeAt(i);
        }
        return LOGO_COLORS[sum % LOGO_COLORS.length];
      },
      toggle(id, checked) {
        this.selected = checked ? _.uniq(this.selected.concat([id])) : this.selected.filter(item => item !== id);
      },
      toggleAll(checked) {
        let ids = this.selectableRows.map(link => link.link_id);
        this.selected = checked ? _.uniq(this.selected.concat(ids)) : this.selected.filter(id => !ids.includes(id));
      },
      save(items) {
        let self = this;
        this.saving = true;
        piincHttp.put('systems/contract-links/covers', { client: this.getClient.slug, items: items }).then(function(response) {
          self.saving = false;
          let skipped = response.data.skipped.length;
          self.$toastr(skipped ? 'warning' : 'success', skipped
            ? self.$tc('contract_links.skipped', skipped, { count: skipped })
            : self.$tc('contract_links.saved', response.data.updated.length, { count: response.data.updated.length }));
          self.load();
        }, function() {
          self.saving = false;
        });
      },
      confirm(links) {
        this.save(links.map(link => ({ link_id: link.link_id, covers: link.suggestion.covers })));
      },
      setOne(link, value) {
        this.save([{ link_id: link.link_id, covers: value }]);
      }
    },
    created() {
      this.load();
    }
  }
</script>
